/**
 * PROVIDER ARTWORK FOR CLUVIK ADMIN — and the honest statement that there is
 * none yet.
 *
 * ─── THE POSITION, STATED FIRST BECAUSE IT IS THE POINT ──────────────────
 *
 * This repository holds NO licensed provider artwork. Not one image asset
 * exists anywhere under `frontend/` — no PNG, no JPG, no SVG file, nothing.
 * Every mark this module renders is therefore a NEUTRAL CLUVIK MARK standing
 * in for a brand mark we do not have, and it is drawn so that it can never be
 * mistaken for one.
 *
 * The three options were: copy the marks from another repository, redraw them
 * from memory, or say we do not have them. The first two were rejected.
 *
 * WHY NOT COPY. The nearest thing to a canonical source is
 * `frontend/src/pages/Setup/connections/providerLogos.jsx` in
 * sk-amroar/cluvik-platform — a separate repository with a separate build and
 * no shared package, so there is no import to make, only a duplication. And it
 * carries a live trap: its `MetaLogo` constant draws the WHATSAPP speech
 * bubble in WhatsApp green (#25D366). Today it is bound only to `whatsapp_*`
 * slugs, so nothing currently renders wrongly — but the constant is named for
 * the parent company, and the first person to add a `meta_ads` or `facebook`
 * provider will reach for the mark that carries the name and ship WhatsApp
 * green under it. Copying that file copies the trap.
 *
 * Its `InitialTile` fallback has a second, quieter problem worth not
 * reproducing: it picks a hue by hashing the provider's FIRST LETTER, so two
 * unrelated providers beginning with the same letter get identical tiles, and
 * a provider's colour changes the day it is renamed. Colour that carries no
 * meaning still reads as though it does.
 *
 * WHY NOT REDRAW. A brand mark redrawn from memory is a mark that is
 * approximately wrong — wrong proportions, wrong swatch, wrong geometry — and
 * it is presented to the operator with all the authority of the real thing. A
 * neutral mark that says "Cluvik has no artwork for this" is less pretty and
 * more true.
 *
 * ─── WHAT REPLACING THIS LOOKS LIKE ──────────────────────────────────────
 *
 * `BRAND_ARTWORK` below is the slot. When a licensed asset for a provider is
 * sourced, it goes in there keyed by provider id and every call site picks it
 * up unchanged — nothing else in the application has to be touched, and
 * `providerArtworkPosition()` starts reporting `brand` for that provider. The
 * map is empty today and that emptiness is asserted by a test, so it cannot
 * quietly acquire a hand-drawn entry.
 *
 * ─── THE DESIGN RULE THIS OBEYS ──────────────────────────────────────────
 *
 * DIMENSIONAL / COLOUR = IDENTITY. FLAT / MONOCHROME = ACTION. A provider is
 * an identity — it names an organisation — so it may carry colour, and the
 * colour it carries is the CLUVIK RAMP, deliberately. A neutral mark wearing
 * Cluvik's own palette reads as Cluvik's stand-in. A neutral mark wearing a
 * plausible-looking brand colour reads as the brand, which is the confusion
 * the whole module exists to avoid.
 *
 * Ramp position is assigned by the provider's INDEX in the served catalogue,
 * not by hashing its name: stable across a rename, and two providers in the
 * catalogue never collide.
 */
import React from 'react';
import { RAMP_KEYS, IDENTITY_SIZES, identityPx } from '../tokens/identityTokens';
import NeutralTileArt, { TILE_VIEWBOX, initialsFrom } from './neutralTile';

/**
 * Licensed brand artwork, keyed by provider id.
 *
 * DELIBERATELY EMPTY. See the header. An entry here must be a real licensed
 * asset used within its owner's brand guidelines — not a redrawing, not a
 * traced path, not "close enough". The test beside this file asserts the map
 * is empty precisely so that adding a hand-drawn mark is a decision somebody
 * has to make on purpose and defend, rather than a diff that slips through.
 */
export const BRAND_ARTWORK = {};

/**
 * The demo-connection catalogue's providers, in the order
 * `connectionProfilePresentation.PROVIDER_ORDER` serves them.
 *
 * THREE, and only three. The demo catalogue (`sample_connections.PROVIDERS`)
 * is a closed enumeration holding `google_workspace`, `microsoft_365` and
 * `aws_s3`. This module does not carry marks for providers that do not exist —
 * a registry with ten entries where the product has three is a promise, and
 * four of the six purpose categories on the Demo Connections screen are empty
 * for exactly that reason.
 */
export const MARKED_PROVIDERS = ['google_workspace', 'microsoft_365', 'aws_s3'];

/**
 * The letters a neutral mark carries.
 *
 * Initials of the provider's OPERATOR-FACING label, so the mark and the words
 * beside it agree — `microsoft_365` is "Microsoft 365" and reads M3, not M.
 * Written down rather than computed: deriving them from the label would make
 * the artwork change when somebody edits a caption.
 */
const NEUTRAL_INITIALS = {
  google_workspace: 'GW',
  microsoft_365: 'M3',
  aws_s3: 'S3',
};

/** Ramp position by catalogue index — stable across renames, never colliding. */
const rampForProvider = (providerId) => {
  const at = MARKED_PROVIDERS.indexOf(String(providerId || ''));
  if (at === -1) return null;
  return RAMP_KEYS[at % RAMP_KEYS.length];
};

/**
 * Initials for a provider outside the catalogue.
 *
 * Mechanical, and the rule lives in `neutralTile.jsx` because the tenant tile
 * needs exactly the same one: the first letter of each of the first two words.
 * It guesses nothing about what the provider IS — every richer rule is an
 * assumption about a vendor this build has never been told about.
 */
const fallbackInitials = (providerId, label) => initialsFrom(label || providerId);

/**
 * What kind of artwork this build actually has for a provider.
 *
 * `'brand'`   a licensed asset — none today.
 * `'neutral'` a Cluvik stand-in for a catalogued provider.
 * `'unknown'` a Cluvik stand-in for a provider outside the catalogue.
 *
 * Exported so a caller — or a test, or a person auditing the screen — can ask
 * the question directly rather than inferring it from what got rendered.
 */
export const providerArtworkPosition = (providerId) => {
  const id = String(providerId || '');
  if (Object.prototype.hasOwnProperty.call(BRAND_ARTWORK, id)) return 'brand';
  return MARKED_PROVIDERS.includes(id) ? 'neutral' : 'unknown';
};

/**
 * A provider's mark.
 *
 * CONTRAST IS PART OF THE GEOMETRY, not a coat of paint. The initials are
 * white on the ramp's DARKEST stop (`RAMP[k][2]`), which clears 4.5:1 at every
 * one of the eight positions — checked arithmetically in the test beside this
 * file, so a future ramp edit that breaks it fails a build rather than shipping
 * unreadable. That is also why there is no gradient behind the letters: a
 * gradient makes "the contrast" a range rather than a number, and the light end
 * of the range is where the text stops being legible.
 *
 * @param {string} provider  Provider id, e.g. `google_workspace`.
 * @param {string} label     Operator-facing name; used for the fallback
 *                           initials and for the accessible name.
 * @param {string|number} size  Identity size token or pixels. Never below xs.
 * @param {boolean} labelled Give the mark an accessible name. Leave OFF when a
 *                           visible label sits beside it — announcing the same
 *                           provider twice is worse than announcing it once.
 */
export default function ProviderMark({
  provider, label, size = 'sm', labelled = false, className,
}) {
  const px = Math.max(identityPx(size), IDENTITY_SIZES.xs);
  const position = providerArtworkPosition(provider);

  const Brand = BRAND_ARTWORK[String(provider || '')];
  if (Brand) {
    // A licensed mark is rendered as its owner drew it, undistorted, and this
    // component adds nothing to it — no tint, no ramp, no Cluvik tile.
    return <Brand width={px} height={px} className={className} aria-hidden={!labelled} />;
  }

  const rampKey = rampForProvider(provider) || 'blue';
  const initials = NEUTRAL_INITIALS[String(provider || '')]
    || fallbackInitials(provider, label);

  return (
    <svg
      width={px}
      height={px}
      viewBox={`0 0 ${TILE_VIEWBOX} ${TILE_VIEWBOX}`}
      className={className}
      role={labelled ? 'img' : 'presentation'}
      aria-label={labelled ? `${label || provider} — no brand artwork available` : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
      data-provider={String(provider || '')}
      /* The machine-readable statement of the position above. A screenshot
         diff cannot tell a stand-in from a brand mark; this attribute can, and
         the test suite reads it. */
      data-artwork={position}
    >
      <title>{`${label || provider || 'Provider'} (Cluvik placeholder mark)`}</title>
      {/* ONE GEOMETRY, drawn in `neutralTile.jsx` and shared with the tenant
          tile. The contrast rule that picks the ramp's darkest stop lives with
          the drawing rather than beside it, so it cannot hold in one copy and
          be a coincidence in the other. */}
      <NeutralTileArt rampKey={rampKey} initials={initials} />
    </svg>
  );
}
